import { Box, Button, IconButton, Stack, SxProps, Tooltip, Typography, alpha } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { CustomTextField } from '@/components/shared/CustomField';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { TemplateFieldTypeEnum } from '@/types';
import { containedButtonStyle } from '@/theme';
import { useFeatureFlag } from '@/hooks';
import { FilterCenterFocus as AddLocationIcon } from '@mui/icons-material';
import {
  NOT_APPLICABLE_LABEL,
  PAINT_BLOCK_LABEL,
  UNCERTAIN_REASON_FIELD_NAME,
  UPDATE_LOCATION_LABEL,
  CALCULATION_METHOD_LABEL,
  VIEW_IN_3D_TOOLTIP_LABEL,
} from '@/constants';
import {
  cancelPOIAdd as cancelPOIAddState,
  editPointOfInterest as editPointOfInterestState,
} from '../../../state';
import CancelIcon from '@mui/icons-material/Cancel';
import { abyssColors, primary } from '@/theme/colors';
import { PaintRegionField } from '../Tabs/PaintRegionField';
import { UnitsEnum } from '@/__generated__/graphql';
import { formatFieldValue } from '@/utils';
import { useAtomValue, useSetAtom } from 'jotai';

type ClickEvent = React.MouseEvent<HTMLDivElement, MouseEvent>;

type Props = {
  fields: Array<{
    label: string;
    value: string;
    type?: TemplateFieldTypeEnum;
    valueTooltip?: string;
    unit?: UnitsEnum | undefined | null;
    onClick?: (event: undefined | ClickEvent) => void;
    subtitle?: string;
  }>;
  sx?: SxProps;
};

const linkFields = new Set(['Image Name']);
const truncatableFields = new Set(['Accessibility', UNCERTAIN_REASON_FIELD_NAME]);

const ValuePill = ({ value }: { value: string }) => {
  return (
    <Box
      sx={{
        background: alpha(primary.blue, 0.1),
        borderRadius: '3.2rem',
        padding: '0.2rem 0.6rem',
      }}
    >
      <Typography
        variant="caption"
        style={{ textAlign: 'right', fontWeight: 700, color: primary.blue, lineHeight: 'normal' }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const CalculationMethodField = ({ title, value }: { title: string; value?: string }) => {
  return (
    <Stack
      alignItems="center"
      justifyContent="space-between"
      direction="row"
      key={`${title}-${value}`}
    >
      <Typography
        variant="caption"
        fontWeight="500"
        whiteSpace="nowrap"
        color={abyssColors.primary[400]}
      >
        {title}
      </Typography>
      <ValuePill value={value ?? ''} />
    </Stack>
  );
};

export const FieldViewer = ({ fields, sx }: Props) => {
  const unitSystem = useAtomValue(unitSystemState);
  const editPointOfInterest = useAtomValue(editPointOfInterestState);
  const cancelPOIAdd = useSetAtom(cancelPOIAddState);

  const isBetaUser = useFeatureFlag('beta-user');

  const isUpdatingLocation = useMemo(() => {
    return editPointOfInterest.formState === 'Update-Location';
  }, [editPointOfInterest.formState]);

  const cancelLocationUpdate = useCallback(() => {
    cancelPOIAdd();
  }, [cancelPOIAdd]);

  return (
    <Stack sx={sx} spacing={1}>
      {(fields || []).map(({ label, value, type, unit, valueTooltip, onClick, subtitle }) => {
        const truncate = truncatableFields.has(label) ? { cutoff: 'dynamic' as const } : undefined;

        if (label === UPDATE_LOCATION_LABEL) {
          return (
            <Stack direction="row" justifyContent="space-between" key={label}>
              <Typography
                sx={{ fontSize: '1.2rem', fontWeight: 500, color: abyssColors.primary[400] }}
              >
                {UPDATE_LOCATION_LABEL}
              </Typography>
              <Tooltip title={VIEW_IN_3D_TOOLTIP_LABEL} placement="top" arrow>
                <Box>
                  <Button
                    key={label}
                    startIcon={<AddLocationIcon />}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ ...containedButtonStyle, height: 26, px: 0.7 }}
                    disabled={isUpdatingLocation}
                  >
                    <Box onClick={onClick}>
                      {value === 'true' ? 'Update Location' : 'Add Location'}
                    </Box>
                  </Button>
                  {isUpdatingLocation && (
                    <Tooltip title="Cancel Location Update" arrow placement="top">
                      <IconButton
                        sx={{ p: 0, border: 1, ml: 0.5, borderRadius: '4px' }}
                        onClick={cancelLocationUpdate}
                      >
                        <CancelIcon sx={{ color: abyssColors.primary[500] }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Tooltip>
            </Stack>
          );
        }

        if (label === PAINT_BLOCK_LABEL && value === NOT_APPLICABLE_LABEL && isBetaUser) {
          return <PaintRegionField label={label} key={label} />;
        }

        if (label === CALCULATION_METHOD_LABEL && value !== NOT_APPLICABLE_LABEL) {
          return <CalculationMethodField title={label} value={value} key={`${label}-${value}`} />;
        }

        return (
          <CustomTextField
            key={`${label}-${value}`}
            mode="ReadOnly"
            id={label}
            title={label}
            valueTooltip={valueTooltip}
            value={formatFieldValue({ unitSystem, type, unit, value, isBlisterField: true })}
            onClick={onClick}
            underlined={linkFields.has(label) && value !== 'N/A'}
            truncate={truncate}
            sx={{ color: abyssColors.primary[400] }}
            subtitle={subtitle}
          />
        );
      })}
    </Stack>
  );
};
